import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/config/queryKeys';
import { AppError } from '@/shared/lib/repository/errors';
import { useAuth } from '@/features/auth';
import { savesRepository } from './savesRepository';

export function useArticleSave(articleId: string) {
  const { user } = useAuth();
  const userId = user?.id;
  return useQuery({
    queryKey: queryKeys.save(articleId),
    queryFn: () => savesRepository.isSaved(articleId, userId ?? ''),
    enabled: Boolean(userId),
  });
}

/** Toggle whether the current user has saved the article (optimistic). */
export function useToggleSave(articleId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const key = queryKeys.save(articleId);

  return useMutation({
    mutationFn: async (saved: boolean) => {
      if (!userId) throw new AppError('Sign in to save');
      if (saved) await savesRepository.unsave(articleId, userId);
      else await savesRepository.save(articleId, userId);
    },
    onMutate: async (saved) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<boolean>(key);
      queryClient.setQueryData<boolean>(key, !saved);
      return { prev };
    },
    onError: (_err, _saved, ctx) => {
      if (ctx?.prev !== undefined) queryClient.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
